import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const FEEDBACK_TYPE_CONFIG = {
  problem: { label: 'Problema', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
  improvement: { label: 'Melhoria', bg: '#E3F2FD', border: '#BBDEFB', dot: '#1E88E5', text: '#1565C0' },
};

const FEEDBACK_STATUS_CONFIG = {
  received: { label: 'Recebido', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' },
  triaged: { label: 'Analisado', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
  fixed: { label: 'Corrigido', bg: '#E3F2FD', border: '#BBDEFB', dot: '#1E88E5', text: '#1565C0' },
  released: { label: 'Disponível', bg: '#E8F5E9', border: '#C8E6C9', dot: '#43A047', text: '#2E7D32' },
  answered: { label: 'Respondido', bg: '#E8F5E9', border: '#C8E6C9', dot: '#43A047', text: '#2E7D32' },
  planned: { label: 'No roadmap', bg: '#F3E5F5', border: '#E1BEE7', dot: '#8E24AA', text: '#6A1B9A' },
};

const FEEDBACK_CLASSIFICATION_CONFIG = {
  bug: { label: 'Bug', bg: '#FFEBEE', border: '#FFCDD2', dot: '#E53935', text: '#C62828' },
  improvement: { label: 'Melhoria', bg: '#E3F2FD', border: '#BBDEFB', dot: '#1E88E5', text: '#1565C0' },
  question: { label: 'Dúvida', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' },
  not_bug: { label: 'Não é bug', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' },
};

const NPS_STATUS_CONFIG = {
  new: { label: 'Novo', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' },
  reviewed: { label: 'Revisado', bg: '#E8F5E9', border: '#C8E6C9', dot: '#43A047', text: '#2E7D32' },
};

const NPS_CATEGORY_CONFIG = {
  promoter: { label: 'Promotor', bg: '#E8F5E9', border: '#C8E6C9', dot: '#43A047', text: '#2E7D32' },
  passive: { label: 'Neutro', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
  detractor: { label: 'Detrator', bg: '#FFEBEE', border: '#FFCDD2', dot: '#E53935', text: '#C62828' },
};

/**
 * DotBadge - Indicador de status genérico (bolinha + texto), mesmo estilo de CompanyStatusBadge.
 */
function DotBadge({ config, fallbackLabel }) {
  const cfg = config || { label: fallbackLabel || '—', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.25,
        py: 0.4,
        borderRadius: '20px',
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        width: 'fit-content',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1, color: cfg.text, whiteSpace: 'nowrap' }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

/** FeedbackTypeBadge - Chip de tipo do feedback ('problem'|'improvement') */
export function FeedbackTypeBadge({ type }) {
  return <DotBadge config={FEEDBACK_TYPE_CONFIG[type]} fallbackLabel={type} />;
}

/** FeedbackStatusBadge - Chip de status do pedido (received|triaged|fixed|released|answered|planned) */
export function FeedbackStatusBadge({ status }) {
  return <DotBadge config={FEEDBACK_STATUS_CONFIG[status]} fallbackLabel={status} />;
}

/** FeedbackClassificationBadge - Classificação da equipe (bug|improvement|question|not_bug) */
export function FeedbackClassificationBadge({ classification }) {
  if (!classification) return null;
  return <DotBadge config={FEEDBACK_CLASSIFICATION_CONFIG[classification]} fallbackLabel={classification} />;
}

/** NpsStatusBadge - Chip de status da avaliação NPS ('new'|'reviewed') */
export function NpsStatusBadge({ status }) {
  return <DotBadge config={NPS_STATUS_CONFIG[status]} fallbackLabel={status} />;
}

/** NpsCategoryBadge - Chip de categoria da nota NPS ('promoter'|'passive'|'detractor') */
export function NpsCategoryBadge({ category }) {
  return <DotBadge config={NPS_CATEGORY_CONFIG[category]} fallbackLabel={category} />;
}

export const FEEDBACK_STATUS_OPTIONS = Object.entries(FEEDBACK_STATUS_CONFIG)
  .map(([value, cfg]) => ({ value, label: cfg.label }));

export const FEEDBACK_CLASSIFICATION_OPTIONS = Object.entries(FEEDBACK_CLASSIFICATION_CONFIG)
  .map(([value, cfg]) => ({ value, label: cfg.label }));

export const FEEDBACK_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
];

/** Status que avisam quem enviou (sino + e-mail), uma vez cada. */
export const FEEDBACK_NOTIFY_STATUSES = ['released', 'answered', 'planned'];

export const NPS_STATUS_OPTIONS = [
  { value: 'new', label: 'Novo' },
  { value: 'reviewed', label: 'Revisado' },
];
